'use client';

import { Box, Typography, Paper, Stack, Button, Chip, Link } from '@mui/material';
import {
  CheckCircle as CheckIcon,
  Info as InfoIcon,
  LocalShipping as ShippingIcon,
} from '@mui/icons-material';
import { useState, useEffect } from 'react';
import { buildApiConfig } from '../../../services/api/apiClient';
import { endPointEnums } from '../../../services/api/endpoints';
import useApiCall from '../../../services/api/useApiCall';
import { useTranslation } from '@/kit/useTranslation';

export default function ContractAlertCard() {
  const { t } = useTranslation();
  const { makeApiCall } = useApiCall();
  const [contract, setContract] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchContract = async () => {
      try {
        const config = buildApiConfig(endPointEnums.GET_MY_CONTRACT);
        const res = await makeApiCall(config);
        if (res?.success && res.data) {
          setContract(res.data);
        }
      } catch (err) {
        console.error('[ContractAlert] Failed to fetch contract', err);
      } finally {
        setLoading(false);
      }
    };

    fetchContract();
  }, [makeApiCall]);

  if (loading || !contract) {
    return null;
  }

  const isActive = contract.status === 'ACTIVE';
  const contractName = contract.contract_name || contract.contractName;
  const discount = contract.base_discount_percent || contract.baseDiscountPercent;

  return (
    <Box>
      <Typography
        sx={{
          fontSize: '0.65rem',
          fontWeight: 800,
          mb: 1.25,
          color: '#94a3b8',
          letterSpacing: 0.5,
        }}
      >
        {t('bulkBooking.activePricingPlan')}
      </Typography>

      <Paper
        elevation={isActive ? 1 : 0}
        sx={{
          p: 2.5,
          borderRadius: 2,
          border: '1px solid',
          borderColor: isActive ? '#10b98114' : '#fbbf2414',
          bgcolor: isActive ? '#ecfdf514' : '#fffbeb14',
          background: isActive
            ? 'linear-gradient(135deg, #ecfdf514 0%, #f0fdf414 100%)'
            : 'linear-gradient(135deg, #fffbeb14 0%, #fef3c714 100%)',
        }}
      >
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
          <Box sx={{ mt: 0.5 }}>
            {isActive ? (
              <CheckIcon sx={{ fontSize: '1.4rem', color: '#10b981', opacity: 0.8 }} />
            ) : (
              <InfoIcon sx={{ fontSize: '1.4rem', color: '#f59e0b', opacity: 0.8 }} />
            )}
          </Box>

          <Box sx={{ flex: 1 }}>
            <Typography
              sx={{
                fontSize: '0.9rem',
                fontWeight: 700,
                color: '#111827',
                mb: 0.5,
              }}
            >
              {isActive
                ? t('bulkBooking.activeContractMessage', { name: contractName })
                : t('bulkBooking.contractLabel', { name: contractName })}
            </Typography>

            {isActive ? (
              <Typography
                sx={{
                  fontSize: '0.75rem',
                  color: '#4b5563',
                  mb: 1.5,
                  lineHeight: 1.5,
                }}
              >
                {t('bulkBooking.discountMessage', { discount })}
              </Typography>
            ) : (
              <Typography
                sx={{
                  fontSize: '0.75rem',
                  color: '#4b5563',
                  mb: 1.5,
                  lineHeight: 1.5,
                }}
              >
                {t('bulkBooking.pendingActivationMessage')}{' '}
                <Link href="/my-pricing" sx={{ fontWeight: 600 }}>
                  {t('bulkBooking.activateNow')}
                </Link>
                .
              </Typography>
            )}

            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Chip
                label={t('bulkBooking.discountChip', { discount })}
                size="small"
                color="success"
                variant="outlined"
                sx={{ height: 24 }}
              />
              <Chip
                label={isActive ? t('bulkBooking.activeStatus') : t('bulkBooking.pendingStatus')}
                size="small"
                color={isActive ? 'success' : 'warning'}
                variant="filled"
                sx={{
                  height: 24,
                  bgcolor: isActive ? '#ecfdf5' : '#fffbeb',
                  color: isActive ? '#14532d' : '#5d3a00',
                  fontWeight: 700,
                }}
              />
            </Box>

            {isActive && (
              <Box sx={{ mt: 1.5 }}>
                <Button
                  size="small"
                  variant="text"
                  href="/my-pricing"
                  component="a"
                  sx={{ p: 0, textDecoration: 'none', fontSize: '0.75rem' }}
                >
                  {t('bulkBooking.viewContractDetails')}
                </Button>
              </Box>
            )}
          </Box>
        </Box>
      </Paper>
    </Box>
  );
}
